import { CampaignStat } from "@signalist/shared-types";

/** How the assigned leads of a locked campaign are spread across the sequence (PREC-3786). */
export interface CampaignLockLeadStates {
	/** Assigned, but nothing has gone out to them yet — safe to move to another campaign. */
	scheduled: number;
	/** Received at least one step and the sequence is still running for them. */
	running: number;
	/** The sequence has finished for them (ended, replied, or all steps sent). */
	ended: number;
}

const atLeastZero = (value: number): number => (value > 0 ? value : 0);

/**
 * Split a campaign's assigned leads into the three buckets the lock explainer shows, derived from
 * the counts `list/campaign-stats/:listId` already returns — no extra request, no new API field.
 *
 * - `ended`     = `completed` (ENDED / MESSAGE_SENT / REPLIES / DONE).
 * - `running`   = contacted leads that have not finished yet.
 * - `scheduled` = assigned leads that have never been contacted.
 *
 * The three are meant to partition `totalLeads`, which holds while `completed ⊆ contacted`. A lead
 * that ended without ever being contacted (removed or blacklisted mid-flight) breaks that, so every
 * bucket is clamped at zero rather than rendering a negative count.
 */
export const resolveCampaignLockLeadStates = (
	stat: Pick<CampaignStat, "totalLeads" | "completed" | "contacted"> | undefined
): CampaignLockLeadStates => {
	if (!stat) {
		return { scheduled: 0, running: 0, ended: 0 };
	}

	return {
		scheduled: atLeastZero(stat.totalLeads - stat.contacted),
		running: atLeastZero(stat.contacted - stat.completed),
		ended: atLeastZero(stat.completed),
	};
};
